import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Layout from "@components/layout/Layout";
import WorkflowPanels from "@components/form/WorkflowPanels";
import InfoPanel from "@components/form/InfoPanel";
import ExchangeRateHistory from "@components/form/exchange_rate/ExchangeRateHistory";
import CreateExchangeRateStep1 from "@components/form/exchange_rate/CreateExchangeRateStep1";
import CreateExchangeRateStep2 from "@components/form/exchange_rate/CreateExchangeRateStep2";
import CreateExchangeRateStep3 from "@components/form/exchange_rate/CreateExchangeRateStep3";
import ConfirmModal from "@components/shared/ConfirmModal";
import StepButton from "@components/shared/StepButton";
import WorkflowComplete from "@components/shared/WorkflowComplete";
import { formatPercentage } from "@utils/formatPercentage";
import { formatDate } from "@utils/formatDate";
import {
  CreateExchangeRatePayload,
  createExchangeRates,
} from "@services/exchangeRate";
import CreateAccountingPeriodModal from "@components/modal/CreateAccountingPeriodModal";
import { DraftNote, EMPTY_DRAFT_NOTE } from "@components/notes/NoteComposer";
import { flushNoteStepDraft } from "@components/notes/flushNoteStepDraft";
import { DBTables } from "@utils/constants/dbTables";
import { toastError, toastSuccess, toastWarning } from "@utils/toast";

const CreateExchangeRate: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draftNote, setDraftNote] = useState<DraftNote>(EMPTY_DRAFT_NOTE);
  const [selectedLevel0Resources, setSelectedLevel0Resources] = useState([]);
  const [selectedLevel1Resource, setSelectedLevel1Resource] =
    useState<any>(undefined);
  const [openCreateAccountingPeriodModal, setOpenCreateAccountingPeriodModal] =
    useState(false);
  const [workflowInstance, setWorkflowInstance] = useState<any>({} as any);
  const [exchangeRateData, setExchangeRateData] = useState<Record<string, any>>(
    {
      accountingPeriod: undefined,
      level1Resource: null,
      level0Resources: [],
      rate: null,
      startAt: null,
    },
  );

  const { mutateAsync: saveExchangeRatesData, isLoading } = useMutation<
    any,
    any,
    CreateExchangeRatePayload
  >(exchangeRateData => createExchangeRates(exchangeRateData));

  const handleStep1Save = (step1Data: {
    accountingPeriod: { periodStart: Date; periodEnd: Date };
    level1Resource: any;
    level0Resources: any;
  }) => {
    setExchangeRateData({ ...exchangeRateData, ...step1Data });

    setCurrentStep(1);
  };

  const handleStep1WaterClassesChange = (level0Resources: any) => {
    setSelectedLevel0Resources(level0Resources);
  };

  const handleStep2Save = (step2Data: { rate: number; startAt: Date }) => {
    setExchangeRateData({ ...exchangeRateData, ...step2Data });

    setCurrentStep(2);
  };

  const handleStep3Save = () => {
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      let waterClassIds: string[] = [];

      exchangeRateData.level0Resources.forEach(
        (zone: any) =>
          (waterClassIds = [
            ...waterClassIds,
            ...zone.selectedWaterClasses.map((c: any) => c.id),
          ]),
      );

      const [, workflowInstance] = await saveExchangeRatesData({
        waterClassIds: waterClassIds,
        rate: exchangeRateData.rate,
        startAt: exchangeRateData.startAt,
        accountingPeriodId: exchangeRateData.accountingPeriod?.id,
      });

      // FR-1 invariant: flush optional NoteStep draft AFTER the entity
      // commits. Allocation announcement applies to a single scheme; anchor
      // to WRSHierarchies so the note rolls up via the existing resolver.
      const noteResult = await flushNoteStepDraft(
        draftNote,
        DBTables.WRSHierarchies,
        exchangeRateData.level1Resource?.id,
      );

      setWorkflowInstance(workflowInstance);
      setIsModalOpen(false);

      if (noteResult.kind === "failed") {
        toastWarning(
          t("notes.step.flush_failed", { artifact: t("exchange_rate.new_exchange_rate") }),
        );
      } else {
        toastSuccess(t("exchange_rate.save_succeed"));
      }

      setCurrentStep(3);
    } catch (e: any) {
      toastError(
        <>
          <p>{t("exchange_rate.save_failed")}</p>
          <p>{e?.response?.data?.message || e?.message}</p>
        </>,
      );
      setIsModalOpen(false);
    }
  };

  return (
    <Layout
      permissions={["CreateAllocationAnnouncement"]}
      breadcrumb={[
        {
          label: t("dashboard.dashboard"),
          href: "/polestar",
        },
        {
          label: t("exchange_rate.new_exchange_rate"),
        },
      ]}
      title={t("exchange_rate.new_exchange_rate")}
      accountingPeriodLevel1ResourceId={
        exchangeRateData.level1Resource?.id || selectedLevel1Resource?.id
      }
    >
      <div className="flex gap-4">
        <WorkflowPanels
          itemClassName="border-t"
          selectedStep={currentStep}
          steps={[
            {
              label: (
                <StepButton
                  index={0}
                  currentStep={currentStep}
                  info={
                    <>
                      <dl className="grid grid-cols-1 gap-x-4">
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-bold text-gray-500">
                            {t("common.level1wrs")}:
                          </dt>
                          <dd className="mt-1 text-sm text-gray-500">
                            {exchangeRateData.level1Resource?.name}
                          </dd>
                        </div>
                      </dl>
                      <dl className="grid grid-cols-1 gap-x-4">
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-bold text-gray-500">
                            {t("common.level0wrs")} {t("common.and")}{" "}
                            {t("common.water_classes")}:
                          </dt>
                          {exchangeRateData.level0Resources?.map(
                            (zone: any) => (
                              <dd className="mt-1 text-sm text-gray-500">
                                {zone.identifier} -{" "}
                                {zone.selectedWaterClasses
                                  .map((c: any) => c.name)
                                  .join(" / ")}
                              </dd>
                            ),
                          )}
                        </div>
                      </dl>
                    </>
                  }
                >
                  {t("exchange_rate.select_hierarchy")}
                </StepButton>
              ),
              panel: (
                <CreateExchangeRateStep1
                  accountingPeriod={exchangeRateData.accountingPeriod}
                  level1Resource={exchangeRateData.level1Resource}
                  level0Resources={exchangeRateData.level0Resources}
                  onSave={handleStep1Save}
                  onWaterClassesChange={handleStep1WaterClassesChange}
                  setSelectedLevel1Resource={setSelectedLevel1Resource}
                />
              ),
              infoPanel: (
                <InfoPanel
                  warnings={[
                    ...(selectedLevel1Resource?.id
                      ? [
                          t("exchange_rate.new_accounting_period", {
                            level1ResourceIdentifier:
                              selectedLevel1Resource?.identifier,
                          }),
                        ]
                      : []),
                  ]}
                  actions={[
                    ...(selectedLevel1Resource?.id
                      ? [
                          {
                            action: () => {
                              setOpenCreateAccountingPeriodModal(true);
                            },
                            label: t("accounting_period.create.modal.title"),
                          },
                        ]
                      : []),
                  ]}
                >
                  <div className="space-y-4">
                    <p>{t("exchange_rate.history_title")}</p>
                  </div>
                </InfoPanel>
              ),
            },
            {
              label: (
                <StepButton
                  index={1}
                  currentStep={currentStep}
                  info={
                    <>
                      <dl className="grid grid-cols-1 gap-x-4">
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-bold text-gray-500">
                            {t("exchange_rate.rate")}:
                          </dt>
                          <dd className="mt-1 text-sm text-gray-500">
                            {formatPercentage(exchangeRateData?.rate)}
                          </dd>
                        </div>
                      </dl>
                      <dl className="grid grid-cols-1 gap-x-4">
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-bold text-gray-500">
                            {t("exchange_rate.start_at")}:
                          </dt>
                          <dd className="mt-1 text-sm text-gray-500">
                            {formatDate(exchangeRateData?.startAt)}
                          </dd>
                        </div>
                      </dl>
                    </>
                  }
                >
                  {t("exchange_rate.enter_values")}
                </StepButton>
              ),
              panel: (
                <CreateExchangeRateStep2
                  rate={exchangeRateData.rate}
                  startAt={exchangeRateData.startAt}
                  level0Resources={exchangeRateData.level0Resources}
                  onSave={handleStep2Save}
                  onCancel={() => {
                    setExchangeRateData({
                      ...exchangeRateData,
                      rate: null,
                      startAt: null,
                    });
                    setCurrentStep(0);
                  }}
                  selectedYear={exchangeRateData.accountingPeriod}
                />
              ),
              infoPanel: (
                <InfoPanel heading={t("exchange_rate.history") as string}>
                  <div className="space-y-4">
                    <p>
                      {selectedLevel0Resources?.map((zone: any) => (
                        <ExchangeRateHistory
                          key={zone.id}
                          level0Resource={zone}
                          selectedYear={exchangeRateData.accountingPeriod}
                        />
                      ))}
                    </p>
                  </div>
                </InfoPanel>
              ),
            },
            {
              label: (
                <StepButton index={2} currentStep={currentStep}>
                  {t("exchange_rate.confirm_values")}
                </StepButton>
              ),
              panel: (
                <CreateExchangeRateStep3
                  level1Resource={exchangeRateData.level1Resource}
                  level0Resources={exchangeRateData.level0Resources}
                  rate={exchangeRateData?.rate}
                  startAt={exchangeRateData?.startAt}
                  onSave={handleStep3Save}
                  onEditZones={() => setCurrentStep(0)}
                  onEditRate={() => setCurrentStep(1)}
                  disabled={isLoading}
                  noteValue={draftNote}
                  onNoteChange={setDraftNote}
                />
              ),
              infoPanel: (
                <InfoPanel heading={t("exchange_rate.history") as string}>
                  <div className="space-y-4">
                    <p>
                      {selectedLevel0Resources?.map((zone: any) => (
                        <ExchangeRateHistory
                          key={zone.id}
                          level0Resource={zone}
                          selectedYear={exchangeRateData.accountingPeriod}
                        />
                      ))}
                    </p>
                  </div>
                </InfoPanel>
              ),
            },
            {
              label: (
                <StepButton
                  index={3}
                  currentStep={currentStep}
                  isOnChain={currentStep === 3}
                >
                  {t("exchange_rate.workflow_completed")}
                </StepButton>
              ),
              panel: (
                <div className="flex flex-col grow h-[800px] max-h-screen">
                  <WorkflowComplete references={[workflowInstance?.id]} />
                </div>
              ),
              infoPanel: (
                <InfoPanel successes={[t("exchange_rate.info.complete")]}>
                  <div className="space-y-4"></div>
                </InfoPanel>
              ),
            },
          ]}
        />
      </div>
      <ConfirmModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleSave}
        isSubmitting={isLoading}
      >
        {t("exchange_rate.modal_confirmation")}
      </ConfirmModal>
      {selectedLevel1Resource && (
        <CreateAccountingPeriodModal
          open={openCreateAccountingPeriodModal}
          level1Resource={{
            id: selectedLevel1Resource?.id,
            name: selectedLevel1Resource?.name,
          }}
          onClose={() => {
            setOpenCreateAccountingPeriodModal(false);
          }}
        />
      )}
    </Layout>
  );
};

export default CreateExchangeRate;
